import * as React from "react";
import { Icon } from "@chakra-ui/react";
import { EmrProcedures } from "@medstonetech/slate-icons";
import { FormProvider } from "react-hook-form";
import { Loading, StaffAvatar } from "shared";
import {
  ChartSection,
  ChartSectionHeader,
  ChartRow,
  ChartRowElement,
  ChartRowGroup,
  ChartSubsection,
  ChartSubsectionContent,
} from "modules";
import { useTriageChartProceduresForm } from "../../hooks";
import {
  ACTIONS_COLUMNS_GAP,
  ACTIONS_ROW_TEMPLATE_COLUMNS_4,
  CONTAINER_PADDING_2,
} from "modules/triage/shared/actions/constants";
import { ActionsHeaderDescription } from "modules/triage/shared";
import { usePermissions } from "contexts";

const sectionId = "SPRO";
const chartCode = "T070a";

function N70Procedures() {
  const {
    getIsLoading,
    onSubmit,
    updateIsLoading,
    useFormReturn,
    onSaveAndNext,
  } = useTriageChartProceduresForm({
    chartCode: chartCode,
  });
  const { scope } = usePermissions();
  const isReadOnly = !scope("nurse:proc").isEditable;

  const { getValues } = useFormReturn;
  const sections = getValues("sections");

  if (getIsLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...useFormReturn}>
      <ChartSection
        onSubmit={onSubmit}
        disabled={isReadOnly}
        onRouteChangeSave={(onComplete) => {
          onSubmit();
          onComplete();
        }}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrProcedures} />}
          showNotesBtn={false}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={onSaveAndNext}
        >
          Procedures {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>
        <ChartSubsection content={<ActionsHeaderDescription />} />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[0] && sections[0].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[0] && sections[0].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[0].${chartCode}${sectionId}Q001A001`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="NG"
                    name={`sections[0].${chartCode}${sectionId}Q001A002`}
                    type="option"
                  />
                  <ChartRowElement
                    label="tube"
                    name={`sections[0].${chartCode}${sectionId}Q001A003`}
                    type="option"
                  />
                  <ChartRowElement
                    label="fr."
                    name={`sections[0].${chartCode}${sectionId}Q001A004`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[0].${chartCode}${sectionId}Q001A005`}
                    w="120px"
                    minW="120px"
                    type="input"
                  />
                  <ChartRowElement
                    label="mL return"
                    name={`sections[0].${chartCode}${sectionId}Q001A006`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[0].${chartCode}${sectionId}Q001A007`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[1] && sections[1].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[1] && sections[1].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[1].${chartCode}${sectionId}Q001A008`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="placement confirmed"
                    name={`sections[1].${chartCode}${sectionId}Q001A009`}
                    type="option"
                  />
                  <ChartRowElement
                    label="to suction"
                    name={`sections[1].${chartCode}${sectionId}Q001A010`}
                    type="option"
                  />
                  <ChartRowElement
                    label="low"
                    name={`sections[1].${chartCode}${sectionId}Q001A011`}
                    fontStyle="italic"
                    type="option"
                  />
                  <ChartRowElement
                    label="intermittent"
                    name={`sections[1].${chartCode}${sectionId}Q001A012`}
                    fontStyle="italic"
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[1].${chartCode}${sectionId}Q001A013`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[2] && sections[2].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[2] && sections[2].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[2].${chartCode}${sectionId}Q001A014`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="portable"
                    name={`sections[2].${chartCode}${sectionId}Q001A015`}
                    type="option"
                  />
                  <ChartRowElement
                    label="to Xray"
                    name={`sections[2].${chartCode}${sectionId}Q001A016`}
                    type="option"
                  />
                  <ChartRowElement
                    label="w/:"
                    fontStyle="italic"
                    type="option"
                    name={`sections[2].${chartCode}${sectionId}Q001A080`}
                  />
                  <ChartRowElement
                    label="monitor"
                    name={`sections[2].${chartCode}${sectionId}Q001A017`}
                    type="option"
                  />
                  <ChartRowElement
                    label="nurse"
                    name={`sections[2].${chartCode}${sectionId}Q001A018`}
                    type="option"
                  />
                  <ChartRowElement
                    label="O2"
                    name={`sections[2].${chartCode}${sectionId}Q001A019`}
                    type="option"
                  />
                  <ChartRowElement
                    label="tech"
                    name={`sections[2].${chartCode}${sectionId}Q001A020`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[2].${chartCode}${sectionId}Q001A021`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[3] && sections[3].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[3] && sections[3].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[3].${chartCode}${sectionId}Q001A022`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="return to room"
                    name={`sections[3].${chartCode}${sectionId}Q001A023`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[3].${chartCode}${sectionId}Q001A024`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[4] && sections[4].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[4] && sections[4].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[4].${chartCode}${sectionId}Q001A025`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="to CT"
                    name={`sections[4].${chartCode}${sectionId}Q001A026`}
                    type="option"
                  />
                  <ChartRowElement
                    label="w/:"
                    fontStyle="italic"
                    type="option"
                    name={`sections[4].${chartCode}${sectionId}Q001A080`}
                  />
                  <ChartRowElement
                    label="contrast"
                    name={`sections[4].${chartCode}${sectionId}Q001A027`}
                    type="option"
                  />
                  <ChartRowElement
                    label="parenteral meds"
                    name={`sections[4].${chartCode}${sectionId}Q001A028`}
                    type="option"
                  />
                  <ChartRowElement
                    label="O2"
                    name={`sections[4].${chartCode}${sectionId}Q001A029`}
                    type="option"
                  />
                  <ChartRowElement
                    label="tech"
                    name={`sections[4].${chartCode}${sectionId}Q001A030`}
                    type="option"
                  />
                  <ChartRowElement
                    label="w/:"
                    fontStyle="italic"
                    type="option"
                    name={`sections[4].${chartCode}${sectionId}Q001A081`}
                  />
                  <ChartRowElement
                    label="monitor"
                    name={`sections[4].${chartCode}${sectionId}Q001A031`}
                    type="option"
                  />
                  <ChartRowElement
                    label="nurse"
                    name={`sections[4].${chartCode}${sectionId}Q001A032`}
                    type="option"
                  />
                  <ChartRowElement
                    label="O2"
                    name={`sections[4].${chartCode}${sectionId}Q001A033`}
                    type="option"
                  />
                  <ChartRowElement
                    label="tech"
                    name={`sections[4].${chartCode}${sectionId}Q001A034`}
                    type="option"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[5] && sections[5].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[5] && sections[5].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[5].${chartCode}${sectionId}Q001A035`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="return to room"
                    name={`sections[5].${chartCode}${sectionId}Q001A036`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[5].${chartCode}${sectionId}Q001A037`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[6] && sections[6].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[6] && sections[6].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[6].${chartCode}${sectionId}Q001A038`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="labs drawn"
                    name={`sections[6].${chartCode}${sectionId}Q001A039`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[6].${chartCode}${sectionId}Q001A040`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[7] && sections[7].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[7] && sections[7].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[7].${chartCode}${sectionId}Q001A041`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="ABG drawn"
                    name={`sections[7].${chartCode}${sectionId}Q001A042`}
                    type="option"
                  />
                  <ChartRowElement
                    label="by:"
                    type="option"
                    name={`sections[7].${chartCode}${sectionId}Q001A080`}
                  />
                  <ChartRowElement
                    label="nurse"
                    name={`sections[7].${chartCode}${sectionId}Q001A043`}
                    type="option"
                  />
                  <ChartRowElement
                    label="MD"
                    name={`sections[7].${chartCode}${sectionId}Q001A044`}
                    type="option"
                  />
                  <ChartRowElement
                    label="RT"
                    name={`sections[7].${chartCode}${sectionId}Q001A045`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[7].${chartCode}${sectionId}Q001A046`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[8] && sections[8].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[8] && sections[8].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[8].${chartCode}${sectionId}Q001A047`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="blood CX drawn"
                    name={`sections[8].${chartCode}${sectionId}Q001A048`}
                    type="option"
                  />
                  <ChartRowElement
                    label="by:"
                    fontStyle="italic"
                    type="option"
                    name={`sections[8].${chartCode}${sectionId}Q001A080`}
                  />
                  <ChartRowElement
                    label="tech"
                    name={`sections[8].${chartCode}${sectionId}Q001A049`}
                    type="option"
                  />
                  <ChartRowElement
                    label="nurse"
                    name={`sections[8].${chartCode}${sectionId}Q001A050`}
                    type="option"
                  />
                  <ChartRowElement
                    label="lab"
                    name={`sections[8].${chartCode}${sectionId}Q001A051`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[8].${chartCode}${sectionId}Q001A052`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[9] && sections[9].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[9] && sections[9].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[9].${chartCode}${sectionId}Q001A053`}
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="to cath lab"
                    name={`sections[9].${chartCode}${sectionId}Q001A054`}
                    type="option"
                  />
                  <ChartRowElement
                    label="for PCI"
                    name={`sections[9].${chartCode}${sectionId}Q001A055`}
                    type="option"
                  />
                  <ChartRowElement
                    label="for other"
                    name={`sections[9].${chartCode}${sectionId}Q001A056`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[9].${chartCode}${sectionId}Q001A057`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[10] && sections[10].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[10] && sections[10].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[10].${chartCode}${sectionId}Q001A058`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="12 lead EKG performed"
                    name={`sections[10].${chartCode}${sectionId}Q001A059`}
                    type="option"
                  />
                  <ChartRowElement
                    label="notified"
                    name={`sections[10].${chartCode}${sectionId}Q001A060`}
                    fontStyle="italic"
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[10].${chartCode}${sectionId}Q001A061`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[11] && sections[11].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[11] && sections[11].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[11].${chartCode}${sectionId}Q001A062`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="repeat EKG done x"
                    name={`sections[11].${chartCode}${sectionId}Q001A063`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[11].${chartCode}${sectionId}Q001A064`}
                    w="50px"
                    minW="50px"
                    type="input"
                  />
                  <ChartRowElement
                    name={`sections[11].${chartCode}${sectionId}Q001A065`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[12] && sections[12].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[12] && sections[12].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[12].${chartCode}${sectionId}Q001A066`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    label="Foley"
                    name={`sections[12].${chartCode}${sectionId}Q001A067`}
                    type="option"
                  />
                  <ChartRowElement
                    label="straight cath"
                    name={`sections[12].${chartCode}${sectionId}Q001A068`}
                    type="option"
                  />
                  <ChartRowElement
                    name={`sections[12].${chartCode}${sectionId}Q001A069`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[13] && sections[13].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[13] && sections[13].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[13].${chartCode}${sectionId}Q001A070`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[13].${chartCode}${sectionId}Q001A071`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[14] && sections[14].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[14] && sections[14].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[14].${chartCode}${sectionId}Q001A072`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[14].${chartCode}${sectionId}Q001A073`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[15] && sections[15].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[15] && sections[15].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[15].${chartCode}${sectionId}Q001A074`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[15].${chartCode}${sectionId}Q001A075`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[16] && sections[16].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[16] && sections[16].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[16].${chartCode}${sectionId}Q001A076`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[16].${chartCode}${sectionId}Q001A077`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
        <ChartSubsection
          content={
            <ChartSubsectionContent
              padding={CONTAINER_PADDING_2}
              whiteSpace="nowrap"
            >
              <ChartRow
                gridTemplateColumns={ACTIONS_ROW_TEMPLATE_COLUMNS_4}
                columnGap={ACTIONS_COLUMNS_GAP}
              >
                <ChartRowGroup justifyContent="center">
                  <StaffAvatar
                    maxWidth="32px"
                    size="xs"
                    fontSize=".25rem"
                    w="32px"
                    h="32px"
                    userName={
                      (sections[17] && sections[17].createdByFullName) || ""
                    }
                    profileUrl={
                      (sections[17] && sections[17].createdByPictureUrl) || ""
                    }
                    justifySelf="center"
                  />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[17].${chartCode}${sectionId}Q001A078`}
                    w="220px"
                    minW="220px"
                    type="date"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
                <ChartRowGroup>
                  <ChartRowElement
                    name={`sections[17].${chartCode}${sectionId}Q001A079`}
                    type="input"
                  />
                  <ChartRowElement type="empty" />
                </ChartRowGroup>
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
      </ChartSection>
    </FormProvider>
  );
}

export { N70Procedures };
